// Consent-gated Google Ads measurement. Nothing optional loads or is sent until the visitor accepts.
export const ADS_ID = 'AW-18486608053';
export const QUOTE_SEND_TO = 'AW-18486608053/fFXRCO75-KYdELWBje9E';
const KEY = 'mv_consent_v1';
const SENT_KEY = 'mv_quote_conv_sent';
const EXCLUDED = [/^\/admin/, /^\/driver/, /^\/reset/, /^\/cancel\//, /^\/reschedule\//, /^\/paid/, /^\/booked/];

export const isTrackedPath = (path) => !EXCLUDED.some((re) => re.test(path || ''));

export const getConsent = () => {
  try { const c = JSON.parse(localStorage.getItem(KEY) || 'null'); return c && typeof c.ads === 'boolean' ? c : null; } catch { return null; }
};

const listeners = new Set();
export const onConsentChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

const consentState = (c) => ({
  ad_storage: c?.ads ? 'granted' : 'denied',
  ad_user_data: c?.ads ? 'granted' : 'denied',
  ad_personalization: 'denied',
  analytics_storage: c?.analytics ? 'granted' : 'denied',
});

const ensureDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) window.gtag = function gtag() { window.dataLayer.push(arguments); }; // eslint-disable-line prefer-rest-params
};

let loaded = false;
const cleanLocation = () => `${window.location.origin}${window.location.pathname}`;

export const loadGoogleTag = () => {
  const c = getConsent();
  if (loaded || !c?.ads || !isTrackedPath(window.location.pathname)) return false;
  ensureDataLayer();
  window.gtag('consent', 'default', { ...consentState(null), wait_for_update: 500 });
  window.gtag('consent', 'update', consentState(c));
  window.gtag('set', { allow_enhanced_conversions: false, allow_google_signals: false, url_passthrough: false, ads_data_redaction: true });
  window.gtag('js', new Date());
  window.gtag('config', ADS_ID, { send_page_view: false, page_location: cleanLocation(), page_referrer: '', allow_enhanced_conversions: false });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${ADS_ID}`;
  s.id = 'mv-gtag';
  document.head.appendChild(s);
  loaded = true;
  return true;
};

export const setConsent = (ads, analytics = ads) => {
  const c = { ads: !!ads, analytics: !!analytics, at: new Date().toISOString() };
  localStorage.setItem(KEY, JSON.stringify(c));
  if (window.gtag && loaded) window.gtag('consent', 'update', consentState(c));
  if (!c.ads) {
    // Revoked: clear Google Ads cookies set on this domain
    document.cookie.split(';').map(x => x.split('=')[0].trim()).filter(n => /^_gcl|^_gac|^_ga/.test(n))
      .forEach(n => { document.cookie = `${n}=; Max-Age=0; path=/`; document.cookie = `${n}=; Max-Age=0; path=/; domain=.${window.location.hostname.replace(/^www\./, '')}`; });
  } else loadGoogleTag();
  listeners.forEach(fn => fn(c));
  return c;
};

export const trackPageView = () => {
  if (!loaded || !getConsent()?.ads || !isTrackedPath(window.location.pathname)) return;
  window.gtag('event', 'page_view', { send_to: ADS_ID, page_location: cleanLocation(), page_path: window.location.pathname, page_referrer: '' });
};

// Opaque one-way id so the quote UUID (which also works as the customer's link token) never leaves the site.
const opaqueId = async (id) => {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`mv-conv:${id}`));
  return `q_${Array.from(new Uint8Array(buf)).slice(0, 12).map(b => b.toString(16).padStart(2, '0')).join('')}`;
};

const sentIds = () => { try { return JSON.parse(localStorage.getItem(SENT_KEY) || '[]'); } catch { return []; } };

export const trackQuoteConversion = async (quote) => {
  if (!quote?.id || quote.available === false) return false;
  const sent = sentIds();
  if (sent.includes(quote.id)) return false;
  localStorage.setItem(SENT_KEY, JSON.stringify([...sent, quote.id].slice(-50)));
  if (!getConsent()?.ads || !isTrackedPath(window.location.pathname)) return false;
  loadGoogleTag();
  if (!window.gtag) return false;
  const transaction_id = await opaqueId(quote.id);
  window.gtag('event', 'conversion', { send_to: QUOTE_SEND_TO, value: 0, currency: 'GBP', transaction_id, page_location: cleanLocation() });
  return true;
};

// Secondary intent only (a tap, not a completed call/chat). Local dataLayer, no number or personal data.
export const pushIntent = (event, location) => {
  if (!isTrackedPath(window.location.pathname)) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, intent_type: 'click', link_location: location || 'unknown', page_path: window.location.pathname });
};

export const installIntentTracking = () => {
  const handler = (e) => {
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    const where = a.closest('[data-cta-area]')?.getAttribute('data-cta-area') || a.getAttribute('data-testid') || a.closest('header,footer,nav,section')?.tagName.toLowerCase();
    if (href.startsWith('tel:')) pushIntent('phone_click', where);
    else if (/^https:\/\/(wa\.me|api\.whatsapp\.com)\//.test(href)) pushIntent('whatsapp_click', where);
  };
  document.addEventListener('click', handler, true);
  return () => document.removeEventListener('click', handler, true);
};
